/**
 * OverviewPanel — the creator hub's Overview tab. A read-only "what is this
 * game, and how is it doing" dashboard:
 *
 *  1. Game-logic diagram — the GameDesign rendered with the real entity sprites
 *     ({@link GameLogicDiagram}).
 *  2. Engagement — plays / views / likes / comments tiles.
 *  3. Players — who FINISHED a run (+ when + how often), from the member-gated
 *     getCreationInsights callable (Phase 2). Empty → a lonely-arcade mascot.
 *  4. LLM spend — the FULL per-activity token+cost from the same callable
 *     (build/iterate + asset-gen + concept), superseding the build-only
 *     estimate the client can compute from version docs (kept as the instant
 *     fallback while the callable loads or if it fails).
 *  5. Version timeline — every build over time, height = tokens, colored by
 *     outcome, with the active version marked.
 *
 * The version-doc data (diagram, tiles, timeline, fallback build spend) needs
 * no backend; the players list + full cost come from the callable.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  AdminLlmCostLane,
  Creation,
  CreationInsights,
  CreationVersion,
  InsightPlayer,
} from '@djgg/creations';
import { isMemberOrAdmin } from '@djgg/creations';
import { isSuperadmin, toClaims } from '../auth/model.js';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { useSetActiveVersion } from '../hooks/useSetActiveVersion.js';
import { Mascot } from '../components/Mascot.js';
import { visualUsage } from './visualUsage.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { GameLogicDiagram } from './GameLogicDiagram.js';
import { PublishControl } from './PublishControl.js';
import { summarizeBuildSpend, buildTimeline, type TimelinePoint } from './overviewStats.js';

const whole = new Intl.NumberFormat('en-US');
const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

/** Human label for each LLM cost lane. */
const ACTIVITY_LABEL: Record<AdminLlmCostLane, string> = {
  'game-v1': 'First build',
  iteration: 'Iterations',
  assets: 'Asset generation',
  concept: 'Concept',
  other: 'Other',
};

/** A short absolute date for a millis timestamp. */
function dateLabel(ms: number): string {
  return ms ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function StatTile({ value, label }: { value: number; label: string }) {
  return (
    <div className="stat-tile" data-testid="overview-tile">
      <div className="stat-tile__value">{whole.format(value)}</div>
      <div className="stat-tile__label">{label}</div>
    </div>
  );
}

/** Compact token label: 1234 → "1.2k", 1_200_000 → "1.2M". */
function shortTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 100_000 ? 0 : 1)}k`;
  return String(n);
}

const STATUS_COLOR: Record<'live' | 'failed' | 'other', string> = {
  live: 'var(--ok, #35d07f)',
  failed: 'var(--danger, #ff5470)',
  other: 'var(--neon)',
};
const barColor = (s: TimelinePoint['status']): string =>
  s === 'live' ? STATUS_COLOR.live : s === 'failed' ? STATUS_COLOR.failed : STATUS_COLOR.other;

/** A labeled bar chart of builds over time (oldest→newest): height = tokens
 *  used, color = outcome, version number + token count under each bar, a
 *  y-axis peak label, and a color legend so the chart actually reads. The
 *  live version's bar is outlined. */
function VersionTimeline({ points }: { points: TimelinePoint[] }) {
  if (points.length === 0) {
    return <div className="astats-chart-empty">No builds yet.</div>;
  }
  const maxTokens = Math.max(1, ...points.map((p) => p.tokens));
  return (
    <div className="vtl" data-testid="version-timeline">
      <div className="vtl__legend">
        <span className="vtl__key">
          <i className="vtl__swatch" style={{ background: STATUS_COLOR.live }} /> Live
        </span>
        <span className="vtl__key">
          <i className="vtl__swatch" style={{ background: STATUS_COLOR.failed }} /> Failed
        </span>
        <span className="vtl__key">
          <i className="vtl__swatch" style={{ background: STATUS_COLOR.other }} /> In&nbsp;progress
        </span>
        <span className="vtl__key">
          <i className="vtl__swatch vtl__swatch--ring" /> Currently&nbsp;live
        </span>
        <span className="vtl__key vtl__key--axis">peak {shortTokens(maxTokens)} tokens</span>
      </div>
      <div className="vtl__plot">
        {points.map((p) => (
          <div className="vtl__col" key={p.n} title={`v${p.n} · ${p.status} · ${whole.format(p.tokens)} tokens · ${p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}`}>
            <div className="vtl__tokens">{p.tokens > 0 ? shortTokens(p.tokens) : ''}</div>
            <div className="vtl__bar-track">
              <div
                className={`vtl__bar${p.active ? ' vtl__bar--active' : ''}`}
                style={{ height: `${Math.max(3, (p.tokens / maxTokens) * 100)}%`, background: barColor(p.status) }}
              />
            </div>
            <div className={`vtl__vlabel${p.active ? ' vtl__vlabel--active' : ''}`}>v{p.n}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Compact "which build is active" control — a live version can be pinned
 * active (rollback/roll-forward) even when it isn't the latest. Builds
 * withhold the active pointer automatically when their headless playability
 * probe disagrees with a working prior version (buildWorker's withhold-on-
 * playability rule) — this is how a creator overrides that from Overview.
 */
function ActiveVersionControl({
  creation,
  versions,
  canManage,
}: {
  creation: Creation & { id: string };
  versions: Array<CreationVersion & { id: string }>;
  canManage: boolean;
}) {
  const { runAttributed, attributionModal } = useAttributedAction();
  const { setActive, activatingVersion, error } = useSetActiveVersion(creation, runAttributed);
  const liveVersions = useMemo(
    () => versions.filter((v) => v.buildStatus === 'live').sort((a, b) => b.n - a.n),
    [versions],
  );
  const [picked, setPicked] = useState<number>(creation.activeVersion);
  useEffect(() => setPicked(creation.activeVersion), [creation.activeVersion]);

  if (liveVersions.length === 0) return null;

  // `versions` is newest-first (the subscription's native order).
  const latest = versions[0];
  const latestWithheld =
    !!latest && latest.buildStatus === 'live' && latest.n !== creation.activeVersion;

  return (
    <div
      className="row"
      style={{ gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}
      data-testid="active-version-control"
    >
      <span style={{ fontSize: 13 }}>
        Active: <strong>v{creation.activeVersion || '—'}</strong>
      </span>
      {latestWithheld && (
        <span className="muted" style={{ fontSize: 12 }} data-testid="active-version-withheld-note">
          v{latest.n} built and went live but isn’t active yet.
        </span>
      )}
      {canManage && (
        <>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={picked}
            onChange={(e) => setPicked(Number(e.target.value))}
            disabled={activatingVersion !== null}
            data-testid="active-version-select"
          >
            {liveVersions.map((v) => (
              <option key={v.n} value={v.n}>
                v{v.n}
                {v.n === creation.activeVersion ? ' (active)' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setActive(picked)}
            disabled={activatingVersion !== null || picked === creation.activeVersion}
            data-testid="active-version-submit"
          >
            {activatingVersion === picked ? 'Setting…' : 'Set active'}
          </button>
        </>
      )}
      {error && (
        <span className="error-text" style={{ fontSize: 12 }}>
          {error}
        </span>
      )}
      {attributionModal}
    </div>
  );
}

/** One player row: avatar + handle (linked to their profile), modes, runs, last seen. */
function PlayerRow({ player }: { player: InsightPlayer }) {
  const modeLabel = player.mode === 'both' ? 'solo + online' : player.mode === 'multi' ? 'online' : 'solo';
  return (
    <tr data-testid="player-row">
      <td>
        <span className="player-cell">
          <UserAvatar avatarKey={player.avatarKey} size={26} />
          {player.handle ? (
            <Link to={`/u/${player.handle}`} className="player-cell__handle">
              @{player.handle}
            </Link>
          ) : (
            <span className="player-cell__handle muted">a player</span>
          )}
        </span>
      </td>
      <td>{modeLabel}</td>
      <td>{whole.format(player.runs)}</td>
      <td>{dateLabel(player.lastAt)}</td>
    </tr>
  );
}

export function OverviewPanel({ creation }: { creation: Creation & { id: string } }) {
  const services = useServices();
  const { user } = useAuth();
  const creationId = creation.id;
  const canManage = isMemberOrAdmin(toClaims(user), creation);
  const showCost = isSuperadmin(user);

  const versions = useSubscription<Array<CreationVersion & { id: string }>>(
    (cb) => services.subscribe.versions(creationId, cb),
    [],
    [creationId],
  );

  // Members-only insights (players + full LLM cost) — a callable, since it
  // aggregates server-only tables. Load once per creation; degrade gracefully.
  const [insights, setInsights] = useState<CreationInsights | null>(null);
  const [insightsError, setInsightsError] = useState(false);
  useEffect(() => {
    let live = true;
    setInsights(null);
    setInsightsError(false);
    services.callable
      .getCreationInsights({ creationId })
      .then((res) => live && setInsights(res))
      .catch(() => live && setInsightsError(true));
    return () => {
      live = false;
    };
  }, [services, creationId]);

  // Client-side build-only spend — instant, and the fallback when the callable
  // hasn't answered (or failed).
  const buildSpend = useMemo(() => summarizeBuildSpend(versions), [versions]);
  const timeline = useMemo(
    () => buildTimeline(versions, creation.activeVersion),
    [versions, creation.activeVersion],
  );
  // Overview's wording comes from the editable GameDesign, but its pictures
  // must match the version players can actually open. The asset binding lives
  // in that active version's manifest, rather than being guessed from entity
  // names in the whole attached-asset library.
  const activeVersion = useMemo(
    () => versions.find((version) => version.n === creation.activeVersion),
    [versions, creation.activeVersion],
  );
  const liveUsage = activeVersion?.manifestSummary
    ? visualUsage(activeVersion.manifestSummary)
    : undefined;

  const cost = insights?.cost;
  const players = insights?.players ?? [];

  return (
    <div className="panel" data-testid="overview-panel">
      <div className="overview-hero">
        <div>
          <h2 className="panel__title" style={{ marginTop: 0 }}>
            Overview — {creation.title}
          </h2>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            How the game works, and how it’s doing.
          </p>
        </div>
        <Mascot context="studio-overview" size={220} quip={false} />
      </div>

      {/* The publish gate's creator control — visibility state + the flip,
          incl. the pending-audit flow. The control runs the shared publish
          machine (usePublishAction), which owns its own pending-only stamp
          watch, so nothing is drilled through this panel. */}
      <PublishControl creation={creation} />

      <section className="overview-section" aria-label="Game setup shortcuts">
        <h3 className="overview-section__title">Game controls &amp; screen</h3>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Link to="?tab=settings&section=controls">Mobile controls &amp; keyboard</Link>
          <Link to="?tab=settings&section=players">Portrait / landscape &amp; multiplayer modes</Link>
        </div>
        <p className="muted" style={{ fontSize: 13 }}>
          Edit the game’s settings here. Saved design and screen-shape changes apply on the next build.
        </p>
      </section>

      <section className="overview-section">
        <h3 className="overview-section__title">How the game works</h3>
        {creation.gameDesign ? (
          <GameLogicDiagram
            design={creation.gameDesign}
            assets={creation.assets}
            liveUsage={liveUsage}
          />
        ) : (
          <p className="muted">
            This game predates the design doc, so there’s no diagram yet. Add a design in Settings
            and your next build will populate it.
          </p>
        )}
      </section>

      <section className="overview-section">
        <h3 className="overview-section__title">Engagement</h3>
        <div className="stats-grid">
          <StatTile value={creation.playsCount ?? 0} label="Plays" />
          <StatTile value={creation.viewsCount ?? 0} label="Views" />
          <StatTile value={creation.likesCount ?? 0} label="Likes" />
          <StatTile value={creation.commentsCount ?? 0} label="Comments" />
        </div>
      </section>

      <section className="overview-section">
        <h3 className="overview-section__title">Players</h3>
        {insights === null && !insightsError ? (
          <p className="muted overview-note">Loading players…</p>
        ) : players.length === 0 ? (
          <div className="empty" data-testid="players-empty">
            <Mascot context="overview-no-plays" size={300} />
            No one’s finished a run yet — share it and be the first name here.
          </div>
        ) : (
          <>
            <div className="table__scroll">
              <table className="table" data-testid="players-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Mode</th>
                    <th>Runs</th>
                    <th>Last played</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => (
                    <PlayerRow key={p.uid} player={p} />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted overview-note">
              {whole.format(players.length)} signed-in player{players.length === 1 ? '' : 's'} who
              finished at least one run. Guests and unfinished sessions aren’t named here but count
              toward Plays above.
            </p>
          </>
        )}
      </section>

      <section className="overview-section">
        <h3 className="overview-section__title">{showCost ? 'LLM spend' : 'Tokens used'}</h3>
        {cost ? (
          <>
            <div className="stats-grid">
              <StatTile value={cost.totalInputTokens} label="Tokens in" />
              <StatTile value={cost.totalOutputTokens} label="Tokens out" />
              {showCost && <div className="stat-tile" data-testid="overview-cost">
                <div className="stat-tile__value">{usd.format(cost.totalCostUsd)}</div>
                <div className="stat-tile__label">Estimated cost</div>
                <div className="stat-tile__sub">all activities</div>
              </div>}
            </div>
            {cost.byActivity.length > 0 && (
              <div className="table__scroll" style={{ marginTop: 12 }}>
                <table className="table" data-testid="cost-table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Calls</th>
                      <th>Tokens in</th>
                      <th>Tokens out</th>
                      {showCost && <th>Cost</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {cost.byActivity.map((row) => (
                      <tr key={row.activity}>
                        <td>{ACTIVITY_LABEL[row.activity] ?? row.activity}</td>
                        <td>{whole.format(row.calls)}</td>
                        <td>{whole.format(row.inputTokens)}</td>
                        <td>{whole.format(row.outputTokens)}</td>
                        {showCost && <td>{usd.format(row.costUsd)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="muted overview-note">
              {showCost
                ? 'Every LLM call charged to this game — builds, iterations, asset generation and concept analysis — estimated from model pricing.'
                : 'Tokens used for this game across builds, changes, art and concept analysis.'}
            </p>
          </>
        ) : (
          // Fallback: the client-side build-only estimate while the callable
          // loads, or if it failed.
          <>
            <div className="stats-grid">
              <StatTile value={buildSpend.inputTokens} label="Tokens in" />
              <StatTile value={buildSpend.outputTokens} label="Tokens out" />
              {showCost && <div className="stat-tile" data-testid="overview-cost">
                <div className="stat-tile__value">
                  {buildSpend.allPriced ? '' : '≈ '}
                  {usd.format(buildSpend.costUsd)}
                </div>
                <div className="stat-tile__label">Estimated cost</div>
                <div className="stat-tile__sub">builds only</div>
              </div>}
            </div>
            <p className="muted overview-note">
              {insightsError
                ? 'Showing builds and changes only — the full breakdown couldn’t load.'
                : 'Showing builds and changes while the full breakdown loads…'}
            </p>
          </>
        )}
      </section>

      <section className="overview-section">
        <h3 className="overview-section__title">Version timeline</h3>
        <ActiveVersionControl creation={creation} versions={versions} canManage={canManage} />
        <VersionTimeline points={timeline} />
        <p className="muted overview-note">
          One bar per build, oldest to newest — height is tokens used, color is the outcome (live,
          failed, in&nbsp;progress). The outlined bar is the version currently live.
        </p>
      </section>
    </div>
  );
}
